/**
 * Master pitch / rate adapter built on Signalsmith Stretch.
 *
 * The vendored module at `vendor/SignalsmithStretch.mjs` registers an
 * `AudioWorkletProcessor` named `signalsmith-stretch` and exports an async
 * factory that:
 *
 *   - Loads the worklet via a Blob URL (no extra fetch, no extra build step).
 *   - Returns an `AudioWorkletNode` augmented with method shims that postMessage
 *     into the worklet (`setTransposeSemitones`, `setFormantSemitones`, …).
 *   - Exposes `inputTime` (seconds of input consumed by the stretch) for
 *     position tracking.
 *
 * Consumers of this module never deal with the worklet directly. They get
 * the simple two-knob `MasterStretchAdapter` interface: pitch in semitones and
 * a playback-rate factor that is applied to upstream sources, with the stretch
 * compensating for the pitch shift those sources introduce.
 *
 * Real-time rules (see `.cursor/rules/50-audio.mdc`):
 *
 *   - This file runs on the control thread only.
 *   - Worklet-side code is in `vendor/SignalsmithStretch.mjs` — vendored, do
 *     not edit.
 *   - We deliberately do not insert/remove the stretch node mid-playback —
 *     graph surgery happens at construction or while stopped.
 */

// `vendor/SignalsmithStretch.mjs` is a pre-compiled Emscripten bundle and does
// not ship type information. Treat it as an opaque async factory and validate
// the surface we use at runtime instead.
// @ts-expect-error — vendored module, no .d.ts ships with it
import SignalsmithStretchFactory from "../vendor/SignalsmithStretch.mjs";

type SignalsmithStretchNode = AudioWorkletNode & {
  setTransposeSemitones: (semitones: number, tonalityHz: number) => Promise<unknown>;
  schedule: (params: {
    outputTime?: number;
    semitones?: number;
    tonalityHz?: number;
    active?: boolean;
    rate?: number;
  }) => Promise<unknown>;
  setFormantSemitones?: (semitones: number, compensate: number) => Promise<unknown>;
  inputTime?: number;
};

type Factory = (
  audioContext: AudioContext,
  options?: AudioWorkletNodeOptions,
) => Promise<SignalsmithStretchNode>;

export interface MasterStretchAdapter {
  /** The underlying AudioWorkletNode — connect upstream into this. */
  readonly node: AudioWorkletNode;
  /** Configure pitch + rate. Both are control-thread state; cheap to call. */
  apply(opts: { pitchSemitones: number; playbackRate: number }): void;
  /** Disconnect the worklet from the graph and release resources. */
  dispose(): void;
}

/**
 * Load the Signalsmith Stretch worklet for `ctx` and return a thin adapter.
 *
 * This is the single side-effecting entry point for pitch/rate; everything
 * else operates on the returned adapter.
 */
export async function loadMasterStretch(ctx: AudioContext): Promise<MasterStretchAdapter> {
  const factory = SignalsmithStretchFactory as unknown as Factory;
  const node = await factory(ctx, {
    numberOfInputs: 1,
    numberOfOutputs: 1,
    outputChannelCount: [2],
  });

  // Tonality limit in Hz (passed through to the WSOLA core). 8 kHz matches
  // the openDAW / sheetmuse-studio default and preserves vocal clarity at
  // moderate transposition.
  const TONALITY_HZ = 8000;

  let lastSemitones = Number.NaN;
  let disposed = false;

  return {
    node,
    apply({ pitchSemitones, playbackRate }) {
      if (disposed) return;
      // The upstream sources play at `playbackRate`, which raises perceived
      // pitch by log2(rate) octaves (= log2(rate)*12 semitones). Compensate
      // here so the user's pitch knob is decoupled from the rate knob.
      const compensation = Math.log2(playbackRate) * 12;
      const target = pitchSemitones - compensation;
      if (target === lastSemitones) return;
      lastSemitones = target;

      if (typeof node.setTransposeSemitones === "function") {
        // Older/newer builds expose direct transpose controls.
        const tonalityNorm = TONALITY_HZ / ctx.sampleRate;
        void node.setTransposeSemitones(target, tonalityNorm).catch((err: unknown) => {
          console.warn("[masterStretch] setTransposeSemitones failed", err);
        });
        return;
      }

      if (typeof node.schedule === "function") {
        // Vendored openDAW-style build uses a timeline API (`schedule`) for
        // pitch/rate updates instead of direct setters.
        void node
          .schedule({
            outputTime: ctx.currentTime,
            semitones: target,
            tonalityHz: TONALITY_HZ,
            active: true,
            rate: 1,
          })
          .catch((err: unknown) => {
            console.warn("[masterStretch] schedule(semitones) failed", err);
          });
        return;
      }

      throw new Error("Signalsmith Stretch worklet did not expose transpose controls");
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      try {
        node.disconnect();
      } catch {
        // already disconnected — ignore.
      }
    },
  };
}
